import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Power, QrCode } from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../../lib/utils';
import { useFetchEvents, useUpdateEvent } from '../../../../services/leads';
import PartnerQrModal from '../../partners/PartnerQrModal';
import { ConfirmModal } from '../../partners/ReviewActionModals';
import EventModal from './EventModal';

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

// "Jul 12 - Jul 13, 2026" from the event's date columns (either may be null).
const eventDatesLabel = (ev) => {
  const fmt = (v) => {
    if (!v) return null;
    const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
    const MONTHS = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return { text: `${MONTHS[m - 1]} ${d}`, year: y };
  };
  const s = fmt(ev.eventStartsAt);
  const e = fmt(ev.eventEndsAt);
  if (!s && !e) return null;
  if (s && e) {
    return s.text === e.text && s.year === e.year
      ? `${s.text}, ${s.year}`
      : `${s.text} - ${e.text}, ${e.year}`;
  }
  const only = s || e;
  return `${s ? 'From' : 'Through'} ${only.text}, ${only.year}`;
};

const Th = ({ children, className }) => (
  <th
    className={cn(
      'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground',
      className,
    )}
  >
    {children}
  </th>
);

// Operations > Leads > Events: in-person lead channels (expos, booths). Each
// event gets a QR + scan-tracked link; veterans who scan land on the
// /event/:slug capture page and become leads attributed to the event. Events
// live on the partners table (partnerType='event') but are managed HERE under
// leads permissions — they have no payee, no portal, no payouts.
const EventsTab = ({ canManage }) => {
  const queryClient = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [toggling, setToggling] = useState(null); // event pending (de)activation confirm

  const { data, isLoading, isError, refetch } = useFetchEvents(
    { includeInactive: includeInactive ? 'true' : 'false' },
    { keepPreviousData: true },
  );
  const rows = data?.rows || [];

  const onError = (err) => toast.error(err?.response?.data?.msg || err.message);
  const toggleEvent = useUpdateEvent(
    () => {
      const wasActive = toggling?.partnerActive;
      toast.success(wasActive ? 'Event deactivated.' : 'Event reactivated.');
      setToggling(null);
      queryClient.invalidateQueries(['leads-events']);
    },
    {
      onError: (err) => {
        setToggling(null);
        onError(err);
      },
    },
  );

  const copyLink = (slug) => {
    const url = `${window.location.protocol}//${window.location.host}/event/${slug}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success('Event link copied.'))
      .catch(() => toast.error('Could not copy the link.'));
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border"
          />
          Include inactive events
        </label>
        {canManage ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Add Event
          </button>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {/* Column separators in the header row only (Nic's call 2026-07-30). */}
        <table className="w-full text-sm [&_th]:border-r [&_th]:border-border [&_th:last-child]:border-r-0">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <Th>Event</Th>
              <Th>Link</Th>
              <Th>Performance</Th>
              <Th>Created By</Th>
              <Th className="text-center">Status</Th>
              <Th className="w-1 text-center">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {isError ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <span className="text-muted-foreground">
                    Couldn&apos;t load events.
                  </span>{' '}
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="font-medium text-primary hover:underline"
                  >
                    Retry
                  </button>
                </td>
              </tr>
            ) : isLoading && !data ? (
              <tr>
                <td colSpan={6} className="px-4 py-6">
                  <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  No events yet.
                  {canManage
                    ? ' Add one to get a QR code you can print for the booth.'
                    : ''}
                </td>
              </tr>
            ) : (
              rows.map((ev) => (
                <tr
                  key={ev.partnerId}
                  className={cn(
                    'border-b border-border last:border-0 hover:bg-accent/40',
                    !ev.partnerActive && 'opacity-60',
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">
                      {ev.partnerName}
                    </div>
                    {eventDatesLabel(ev) ? (
                      <div className="text-xs text-muted-foreground">
                        {eventDatesLabel(ev)}
                      </div>
                    ) : null}
                    {ev.partnerDetails ? (
                      <div className="max-w-[260px] truncate text-xs text-muted-foreground">
                        {ev.partnerDetails}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-foreground">
                        /event/{ev.partnerReferralUrl}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyLink(ev.partnerReferralUrl)}
                        title="Copy event link"
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {ev.partnerCode ? (
                      <div
                        className="text-xs text-muted-foreground"
                        title="Scan-tracked short link — what this event's QR code encodes."
                      >
                        QR link: /r/{ev.partnerCode}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-muted-foreground">
                      {ev.statScans ?? 0} scans · {ev.statVisits ?? 0} visits
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {ev.statLeads ?? 0} leads · {ev.statConversions ?? 0}{' '}
                      {(ev.statConversions ?? 0) === 1 ? 'client' : 'clients'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {ev.partnerCreatedByName || '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        CHIP,
                        ev.partnerActive
                          ? 'bg-status-success-bg text-status-success-fg'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {ev.partnerActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setQrFor(ev)}
                        title="View QR code"
                        className="rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      >
                        <QrCode className="h-4 w-4" />
                      </button>
                      {canManage ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditing(ev)}
                            title="Edit event"
                            className="rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setToggling(ev)}
                            title={
                              ev.partnerActive
                                ? 'Deactivate (QR stops attributing)'
                                : 'Reactivate'
                            }
                            className={cn(
                              'rounded-md border border-border p-1.5 text-muted-foreground transition-colors',
                              // Hover telegraphs the outcome: red = turning
                              // off, green = turning back on.
                              ev.partnerActive
                                ? 'hover:border-status-error/40 hover:bg-status-error-bg hover:text-status-error-fg'
                                : 'hover:border-status-success/40 hover:bg-status-success-bg hover:text-status-success-fg',
                            )}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? <EventModal onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <EventModal
          key={editing.partnerId}
          event={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {qrFor ? (
        <PartnerQrModal partner={qrFor} onClose={() => setQrFor(null)} />
      ) : null}
      {toggling ? (
        <ConfirmModal
          title={
            toggling.partnerActive ? 'Deactivate event' : 'Reactivate event'
          }
          body={
            toggling.partnerActive
              ? `Deactivate "${toggling.partnerName}"? Its QR code and link stop attributing new leads and show a "no longer active" notice. You can reactivate anytime.`
              : `Reactivate "${toggling.partnerName}"? Its QR code and link start attributing leads again immediately.`
          }
          confirmLabel={toggling.partnerActive ? 'Deactivate' : 'Reactivate'}
          destructive={Boolean(toggling.partnerActive)}
          isSaving={toggleEvent.isLoading}
          onClose={() => setToggling(null)}
          onConfirm={() =>
            toggleEvent.mutate({
              partnerId: toggling.partnerId,
              active: !toggling.partnerActive,
            })
          }
        />
      ) : null}
    </div>
  );
};

export default EventsTab;
